import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Card, Badge } from 'react-bootstrap';
import { KTIcon } from '../../../../_metronic/helpers';
import { Asset } from '../types/assetTypes';
import { toast } from 'react-toastify';
import {
  get_all_projects_for_download,
  get_all_subprojects_for_download,
  get_active_subprojects_of_project,
  download_asset_with_licenses
} from '../api/downloadAssetsApi';
import { get_single_asset } from '../api/assetsApi';
import { downloadFileFromWasabi } from '../services/wasabiUploadService';

interface AssetDownloadModalProps {
  show: boolean;
  onHide: () => void;
  asset: Asset | null;
}

interface ProjectItem {
  id: number;
  project_name: string;
}

interface SubprojectItem {
  id: number;
  subproject_name: string;
}

interface LicenseFile {
  file: File;
  license_date?: string;
  license_text?: string;
}

const AssetDownloadModal: React.FC<AssetDownloadModalProps> = ({ show, onHide, asset }) => {
  const [assetData, setAssetData] = useState<any>(null);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [useNewLicense, setUseNewLicense] = useState(false);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [subprojects, setSubprojects] = useState<SubprojectItem[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [selectedSubproject, setSelectedSubproject] = useState<number | null>(null);
  const [licenseFiles, setLicenseFiles] = useState<LicenseFile[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingSubprojects, setIsLoadingSubprojects] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string>('');
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load asset detail when modal opens
  useEffect(() => {
    if (show && asset?.id) {
      loadAssetDetail();
      setUseNewLicense(false);
      setSelectedProject(null);
      setSelectedSubproject(null);
      setLicenseFiles([]);
    }
  }, [show, asset?.id]);

  // Load projects when useNewLicense is checked
  useEffect(() => {
    if (useNewLicense) {
      loadProjects();
    }
  }, [useNewLicense]);

  // Load subprojects when project is selected
  useEffect(() => {
    if (selectedProject && useNewLicense) {
      loadSubprojects(selectedProject);
      setSelectedSubproject(null);
      setLicenseFiles([]);
    } else {
      setSubprojects([]);
      setSelectedSubproject(null);
    }
  }, [selectedProject, useNewLicense]);

  const loadAssetDetail = async () => {
    if (!asset?.id) return;
    
    try {
      setIsLoadingAsset(true);
      const response = await get_single_asset(asset.id);
      const uploadData = response?.data?.upload_data || response?.upload_data || response?.data || null;
      setAssetData(uploadData);
    } catch (error) {
      console.error('Error loading asset detail:', error);
      toast.error('Failed to load asset details');
    } finally {
      setIsLoadingAsset(false);
    }
  };

  const loadProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const response = await get_all_projects_for_download();
      const apiProjects = response?.data?.all_projects || response?.data?.projects || response?.data || [];
      const allProjects: ProjectItem[] = (Array.isArray(apiProjects) ? apiProjects : []).map((p: any) => ({
        id: Number(p.id),
        project_name: p.project_name || p.name || ''
      }));
      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadSubprojects = async (projectId: number) => {
    try {
      setIsLoadingSubprojects(true);
      // Use get_active_subprojects_of_project to get subprojects for the specific project
      const response = await get_active_subprojects_of_project(projectId.toString());
      const apiSubprojects = response?.data?.active_subprojects_of_project || response?.data?.active_subprojects || response?.data?.subprojects || response?.data || [];
      const allSubprojects: SubprojectItem[] = (Array.isArray(apiSubprojects) ? apiSubprojects : []).map((sp: any) => ({
        id: Number(sp.id),
        subproject_name: sp.subproject_name || sp.name || sp.title || ''
      }));
      setSubprojects(allSubprojects);
    } catch (error) {
      console.error('Error loading subprojects:', error);
      toast.error('Failed to load subprojects');
      setSubprojects([]);
    } finally {
      setIsLoadingSubprojects(false);
    }
  };

  const handleProjectChange = (projectId: number | null) => {
    setSelectedProject(projectId);
  };

  const handleSubprojectChange = (subprojectId: number | null) => {
    setSelectedSubproject(subprojectId);
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newFiles: LicenseFile[] = Array.from(files).map(file => ({
      file,
      license_date: '',
      license_text: ''
    }));

    setLicenseFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const removeLicenseFile = (index: number) => {
    setLicenseFiles(prev => prev.filter((_, i) => i !== index));
  };

  const updateLicenseFile = (index: number, field: 'license_date' | 'license_text', value: string) => {
    setLicenseFiles(prev => prev.map((license, i) => 
      i === index ? { ...license, [field]: value } : license
    ));
  };

  // Helper function to construct full URL for license file path
  const getLicenseFileUrl = (filePath: string | null | undefined): string | null => {
    if (!filePath) return null;
    
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    let apiBaseUrl = process.env.REACT_APP_API_URL || '';
    apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    
    const cleanPath = filePath.replace(/^\//, '');
    const encodedFileName = encodeURIComponent(cleanPath);
    
    return `${apiBaseUrl}/licenses/${encodedFileName}`;
  };

  const handleDownloadAsset = async (subprojectId?: number, licenseId?: number) => {
    if (!asset || !asset.id) {
      toast.error('Asset not found');
      return;
    }

    if (!asset.zip_file_url) {
      toast.error('Asset file not available');
      return;
    }

    try {
      setIsDownloading(true);
      setDownloadStatus('Preparing download...');
      setDownloadProgress(30);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setDownloadStatus('Downloading file from Wasabi...');
      setDownloadProgress(60);
      
      await downloadFileFromWasabi(asset.zip_file_url, `${asset.name || 'asset'}.zip`);
      
      setDownloadStatus('Download completed successfully!');
      setDownloadProgress(100);
      
      toast.success('File download started!');
      
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadStatus('');
        setDownloadProgress(0);
      }, 1500);
    } catch (error: any) {
      console.error('Error downloading asset:', error);
      setDownloadStatus('Download failed. Please try again.');
      setDownloadProgress(0);
      toast.error(error?.message || 'Failed to download file. Please try again.');
      setIsDownloading(false);
    }
  };

  const handleDownloadWithNewLicense = async () => {
    if (!asset || !asset.id) {
      toast.error('Asset not found');
      return;
    }

    if (!selectedProject || !selectedSubproject) {
      toast.error('Please select project and subproject');
      return;
    }

    if (licenseFiles.length === 0) {
      toast.error('Please upload at least one license file');
      return;
    }

    const filesWithoutDate = licenseFiles.filter(lf => !lf.license_date || lf.license_date.trim() === '');
    if (filesWithoutDate.length > 0) {
      toast.error('Please provide license date for all license files');
      return;
    }

    try {
      setIsDownloading(true);
      setDownloadStatus('Uploading license files...');
      setDownloadProgress(10);
      
      const licenses = licenseFiles.map(lf => ({
        license_file: lf.file,
        license_date: lf.license_date || undefined,
        license_text: lf.license_text || undefined
      }));

      setDownloadStatus('Attaching licenses to asset...');
      setDownloadProgress(30);
      
      const response = await download_asset_with_licenses(
        asset.id,
        selectedSubproject,
        licenses
      );

      if (!response?.status) {
        throw new Error(response?.message || 'Failed to attach licenses');
      }

      setDownloadStatus('License attached successfully! Preparing download...');
      setDownloadProgress(50);
      
      await new Promise(resolve => setTimeout(resolve, 500));

      const assetFilePath = response?.data?.upload?.asset_file_path || asset.zip_file_url;
      
      if (assetFilePath) {
        setDownloadStatus('Connecting to Wasabi storage...');
        setDownloadProgress(60);
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setDownloadStatus('Downloading file from Wasabi...');
        setDownloadProgress(70);
        
        await downloadFileFromWasabi(assetFilePath, `${asset.name || 'asset'}.zip`);
        
        setDownloadStatus('Download completed successfully!');
        setDownloadProgress(100);
        
        toast.success('File download started!');
        
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setDownloadStatus('License attached, but download URL not available.');
        setDownloadProgress(100);
        toast.warning('License attached successfully, but download URL not available.');
        setIsDownloading(false);
      }
    } catch (error: any) {
      console.error('Error downloading asset:', error);
      setDownloadStatus('Download failed. Please try again.');
      setDownloadProgress(0);
      toast.error(error?.response?.data?.message || 'Failed to download asset');
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    setAssetData(null);
    setUseNewLicense(false);
    setSelectedProject(null);
    setSelectedSubproject(null);
    setLicenseFiles([]);
    setIsDragOver(false);
    setIsDownloading(false);
    setDownloadStatus('');
    setDownloadProgress(0);
    onHide();
  };

  if (!asset) return null;

  const projectHierarchy = assetData?.project_hierarchy || [];

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Download Asset: {asset.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingAsset ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3">Loading asset details...</div>
          </div>
        ) : (
          <>
            {/* Toggle for New License */}
            <div className="mb-4 p-3 bg-light rounded">
              <Form.Check
                type="checkbox"
                id="useNewLicense"
                label="Download with new license"
                checked={useNewLicense}
                onChange={(e) => setUseNewLicense(e.target.checked)}
                className="fw-bold"
              />
              <div className="text-muted fs-7 mt-1">
                {useNewLicense 
                  ? 'Upload new license files and associate with project/subproject'
                  : 'Download asset using existing associations'}
              </div>
            </div>

            {/* Project Hierarchy with Licenses */}
            {!useNewLicense && projectHierarchy.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3 fw-bold">
                  <KTIcon iconName="briefcase" className="me-2" />
                  Project Hierarchy & Licenses
                </h6>
                <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {projectHierarchy.map((project: any) => (
                    <Card key={project.id} className="mb-3">
                      <Card.Body>
                        <div className="mb-2">
                          <h6 className="text-primary mb-1">
                            <KTIcon iconName="folder" className="me-2" />
                            {project.project_name}
                          </h6>
                          {project.description && (
                            <p className="text-muted small mb-0">{project.description}</p>
                          )}
                        </div>
                        
                        {project.subprojects && project.subprojects.length > 0 && (
                          <div>
                            {project.subprojects.map((subproject: any) => (
                              <Card key={subproject.id} className="mb-2 bg-light">
                                <Card.Body className="p-3">
                                  <div className="mb-2">
                                    <strong>
                                      <KTIcon iconName="file-up" className="me-2" />
                                      {subproject.subproject_name}
                                    </strong>
                                  </div>
                                  {subproject.description && (
                                    <div className="text-muted small mb-2">{subproject.description}</div>
                                  )}
                                  
                                  {subproject.licenses && subproject.licenses.length > 0 ? (
                                    <div>
                                      <strong className="d-block mb-2">Licenses:</strong>
                                      {subproject.licenses.map((license: any, idx: number) => (
                                        <div key={idx} className="mb-2 p-2 bg-white rounded border d-flex justify-content-between align-items-center">
                                          <div className="flex-grow-1">
                                            {license.license_file_path && (
                                              <div className="mb-1">
                                                <strong>File:</strong>
                                                <span className="text-muted ms-2">{license.license_file_path}</span>
                                              </div>
                                            )}
                                            {license.license_date && (
                                              <div className="mb-1">
                                                <strong>Date:</strong>
                                                <span className="text-muted ms-2">{new Date(license.license_date).toLocaleDateString()}</span>
                                              </div>
                                            )}
                                            {license.description && (
                                              <div>
                                                <strong>Description:</strong>
                                                <span className="text-muted ms-2">{license.description}</span>
                                              </div>
                                            )}
                                          </div>
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleDownloadAsset(subproject.id, license.id)}
                                            title="Download asset"
                                            disabled={isDownloading}
                                          >
                                            <KTIcon iconName="file-down" className="me-1" />
                                            Download Asset
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-muted small">No licenses available</div>
                                  )}
                                </Card.Body>
                              </Card>
                            ))}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* New License Upload Form */}
            {useNewLicense && (
              <>
                {/* Step 1: Select Project */}
                <div className="mb-4">
                  <label className="form-label fw-bold mb-3">Select Project <span className="text-danger">*</span></label>
                  <select
                    className="form-select form-select-solid"
                    value={selectedProject || ''}
                    onChange={(e) => handleProjectChange(e.target.value ? Number(e.target.value) : null)}
                    disabled={isLoadingProjects}
                  >
                    <option value="">Select Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.project_name}
                      </option>
                    ))}
                  </select>
                  {isLoadingProjects && (
                    <div className="text-muted fs-7 mt-2">
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Loading projects...
                    </div>
                  )}
                </div>

                {/* Step 2: Select Subproject */}
                {selectedProject && (
                  <div className="mb-4">
                    <label className="form-label fw-bold mb-3">Select Subproject <span className="text-danger">*</span></label>
                    <select
                      className="form-select form-select-solid"
                      value={selectedSubproject || ''}
                      onChange={(e) => handleSubprojectChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={isLoadingSubprojects}
                    >
                      <option value="">Select Subproject</option>
                      {subprojects.map(subproject => (
                        <option key={subproject.id} value={subproject.id}>
                          {subproject.subproject_name}
                        </option>
                      ))}
                    </select>
                    {isLoadingSubprojects && (
                      <div className="text-muted fs-7 mt-2">
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Loading subprojects...
                      </div>
                    )}
                    {subprojects.length === 0 && !isLoadingSubprojects && (
                      <div className="text-muted fs-7 mt-2">No subprojects available for this project</div>
                    )}
                  </div>
                )}

                {/* Step 3: Upload License Files */}
                {selectedSubproject && (
                  <div className="mb-4">
                    <label className="form-label fw-bold mb-3">Upload License Files <span className="text-danger">*</span></label>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      onChange={handleFileInputChange}
                      className="d-none"
                    />

                    <div
                      className={`border-2 border-dashed rounded p-5 text-center mb-3 ${
                        isDragOver ? 'border-primary bg-light-primary' : 'border-gray-300'
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <KTIcon iconName="file-up" className="fs-3x text-muted mb-3" />
                      <div className="fw-bold mb-2">Drag & Drop files here</div>
                      <div className="text-muted mb-3">or</div>
                      <Button variant="outline-primary" size="sm" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                        <KTIcon iconName="folder" className="me-2" />
                        Choose Files
                      </Button>
                      <div className="text-muted fs-7 mt-2">You can upload multiple files (PDF, DOC, DOCX, TXT, JPG, PNG)</div>
                    </div>

                    {licenseFiles.length > 0 && (
                      <div className="mt-4">
                        <h6 className="mb-3">Uploaded License Files ({licenseFiles.length})</h6>
                        {licenseFiles.map((licenseFile, index) => (
                          <Card key={index} className="mb-3">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <KTIcon iconName="file" className="fs-4 me-2 text-primary" />
                                    <strong>{licenseFile.file.name}</strong>
                                    <Badge bg="info" className="ms-2">
                                      {(licenseFile.file.size / 1024).toFixed(2)} KB
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() => removeLicenseFile(index)}
                                >
                                  <KTIcon iconName="trash" />
                                </Button>
                              </div>

                              <div className="row g-3">
                                <div className="col-md-6">
                                  <Form.Label>License Date <span className="text-danger">*</span></Form.Label>
                                  <Form.Control
                                    type="date"
                                    value={licenseFile.license_date || ''}
                                    onChange={(e) => updateLicenseFile(index, 'license_date', e.target.value)}
                                    placeholder="Select date"
                                    required
                                    className={!licenseFile.license_date ? 'border-danger' : ''}
                                  />
                                  {!licenseFile.license_date && (
                                    <div className="text-danger fs-7 mt-1">License date is required</div>
                                  )}
                                </div>
                                <div className="col-md-6">
                                  <Form.Label>License Text / Description (Optional)</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={licenseFile.license_text || ''}
                                    onChange={(e) => updateLicenseFile(index, 'license_text', e.target.value)}
                                    placeholder="Enter license description"
                                  />
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Download Progress */}
            {isDownloading && (
              <div className="mb-4 p-4 bg-light rounded">
                <div className="d-flex align-items-center mb-3">
                  <div className="spinner-border spinner-border-sm text-primary me-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold text-primary mb-1">{downloadStatus || 'Processing...'}</div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        role="progressbar"
                        style={{ width: `${downloadProgress}%` }}
                        aria-valuenow={downloadProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      ></div>
                    </div>
                    <div className="text-muted fs-7 mt-2">
                      {downloadProgress < 50 && 'Please wait, this may take a moment...'}
                      {downloadProgress >= 50 && downloadProgress < 100 && 'Downloading from Wasabi storage...'}
                      {downloadProgress === 100 && 'Download completed!'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Download Button - Only show when useNewLicense is checked */}
            {useNewLicense && (
              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  onClick={handleDownloadWithNewLicense}
                  disabled={isDownloading || !selectedProject || !selectedSubproject || licenseFiles.length === 0}
                  size="lg"
                >
                  {isDownloading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <KTIcon iconName="arrow-down" className="fs-5 me-2" />
                      Download Asset
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={handleClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssetDownloadModal;
